import { Form } from 'components/organisms';
import { clientModuleConfigurationInitialValues } from 'components/organisms/forms/settings/ClientModuleConfigurationForm';
import {
  useClientModuleConfiguration,
  useUpdateClientModuleConfiguration,
  useUpdateClientModuleFavicon,
  useUpdateClientModuleLogo,
} from 'services/settings/client-module';
import {
  IClientModuleConfigurationForm,
  IUpdateClientModuleConfiguration,
} from 'services/settings/client-module/types';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const getConfigurationId = (id: unknown): string => {
  if (typeof id === 'string') {
    return id;
  }

  if (id && typeof id === 'object' && 'value' in id) {
    return String((id as { value: string }).value);
  }

  return '';
};

const mapConfigurationToForm = (
  data: NonNullable<
    ReturnType<typeof useClientModuleConfiguration>['data']
  >
): IClientModuleConfigurationForm => ({
  clientName: data.clientName,
  useMagellupLogo: data.useMagellupLogo,
  contractTemplateId: data.contractTemplateId,
  autoCreateAccount: data.autoCreateAccount ?? false,
  clientLogoPath: data.clientLogoPath,
  faviconPath: data.faviconPath,
  magelupLogoPath: data.magelupLogoPath,
  currentLogoPath: data.currentLogoPath,
});

const getConfigurationPayload = (
  values: IClientModuleConfigurationForm
): IUpdateClientModuleConfiguration => ({
  clientName: values.clientName,
  useMagellupLogo: values.useMagellupLogo,
  contractTemplateId: values.contractTemplateId,
  autoCreateAccount: values.autoCreateAccount,
});

const hasConfigurationChanges = (
  values: IClientModuleConfigurationForm,
  initialValues: IClientModuleConfigurationForm
) => {
  const next = getConfigurationPayload(values);
  const previous = getConfigurationPayload(initialValues);

  return (
    next.clientName !== previous.clientName ||
    next.useMagellupLogo !== previous.useMagellupLogo ||
    next.contractTemplateId !== previous.contractTemplateId ||
    next.autoCreateAccount !== previous.autoCreateAccount
  );
};

const ClientModuleConfiguration = () => {
  const clientModuleConfiguration = useClientModuleConfiguration();

  const updateClientModuleConfiguration = useUpdateClientModuleConfiguration();
  const updateClientModuleLogo = useUpdateClientModuleLogo();
  const updateClientModuleFavicon = useUpdateClientModuleFavicon();

  const initialValues = clientModuleConfiguration.data
    ? mapConfigurationToForm(clientModuleConfiguration.data)
    : clientModuleConfigurationInitialValues;

  const onSubmit = async (values: IClientModuleConfigurationForm) => {
    const configurationId = getConfigurationId(
      clientModuleConfiguration.data?.id
    );
    const shouldUpdateConfiguration = hasConfigurationChanges(
      values,
      initialValues
    );

    try {
      let resolvedConfigurationId = configurationId;

      if (shouldUpdateConfiguration) {
        const updatedConfiguration =
          await updateClientModuleConfiguration.mutateAsync(
            getConfigurationPayload(values)
          );
        resolvedConfigurationId =
          getConfigurationId(updatedConfiguration.id) || resolvedConfigurationId;
      }

      const uploadErrors: Error[] = [];

      if (values.file) {
        if (!resolvedConfigurationId) {
          throw new Error('Missing configuration id');
        }

        try {
          await updateClientModuleLogo.mutateAsync({
            file: values.file,
            id: resolvedConfigurationId,
          });
        } catch (error) {
          uploadErrors.push(
            error instanceof Error ? error : new Error('Logo upload failed')
          );
        }
      }

      if (values.faviconFile) {
        if (!resolvedConfigurationId) {
          throw new Error('Missing configuration id');
        }

        try {
          await updateClientModuleFavicon.mutateAsync({
            file: values.faviconFile,
            id: resolvedConfigurationId,
          });
        } catch (error) {
          uploadErrors.push(
            error instanceof Error
              ? error
              : new Error('Favicon upload failed')
          );
        }
      }

      await clientModuleConfiguration.refetch();

      if (uploadErrors.length > 0) {
        onErrorShowAllErrorsToast(uploadErrors[0]);
      }
    } catch (error) {
      await clientModuleConfiguration.refetch();
      if (error instanceof Error) {
        onErrorShowAllErrorsToast(error);
      } else {
        onErrorShowAllErrorsToast(new Error('Unknown error occurred'));
      }
    }
  };

  return (
    <Form.ClientModuleConfiguration
      status={{
        isLoading: clientModuleConfiguration.isLoading,
        displayMode: true,
      }}
      initialValues={initialValues}
      i18nextPrefix='client-module-configuration'
      onSubmit={onSubmit}
    />
  );
};

export default ClientModuleConfiguration;
